import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const XMLParserConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['settings', 'input']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3">
      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="XML Beállítások"
        icon="⚙️"
        color="orange"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="preserveAttributes"
              checked={node.data.config.preserveAttributes !== false}
              onChange={(e) => updateConfig('preserveAttributes', e.target.checked)}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="preserveAttributes" className="text-sm text-gray-700 dark:text-gray-300">
              Attribútumok megtartása
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="ignoreComments"
              checked={node.data.config.ignoreComments !== false}
              onChange={(e) => updateConfig('ignoreComments', e.target.checked)}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="ignoreComments" className="text-sm text-gray-700 dark:text-gray-300">
              Kommentek figyelmen kívül hagyása
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="trimValues"
              checked={node.data.config.trimValues !== false}
              onChange={(e) => updateConfig('trimValues', e.target.checked)}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="trimValues" className="text-sm text-gray-700 dark:text-gray-300">
              Szóközök eltávolítása
            </label>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
            <div className="text-xs text-orange-700 dark:text-orange-300">
              <strong>💡 Attribútumok:</strong> Ha be van kapcsolva, az attribútumok <code className="bg-orange-100 dark:bg-orange-800 px-1 rounded">@attr</code> prefix-szel jelennek meg.
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Input */}
      <CollapsibleSection
        id="input"
        title="XML Tartalom"
        icon="📄"
        color="blue"
        isExpanded={expandedSections.has('input')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              XML szöveg
            </label>
            <textarea
              value={node.data.config.xml || ''}
              onChange={(e) => updateConfig('xml', e.target.value)}
              className={`${commonStyles.textarea} font-mono text-xs`}
              rows={8}
              placeholder={`<?xml version="1.0"?>
<root>
  <item id="1">
    <name>Product</name>
    <price>100</price>
  </item>
</root>

vagy {{xml_data}}`}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              XML szöveg vagy változó
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A feldolgozás után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{json}}', desc: 'JSON objektum', example: '{root: {...}}' },
              { var: '{{parsed}}', desc: 'Feldolgozva', example: 'true' },
              { var: '{{elements}}', desc: 'Elemek száma', example: '15' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📄 XML API:</strong>
            <div className="mt-1 font-mono text-[10px]">
              HTTP Request (XML) → XML Parser → JSON Processor
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔄 Formátum konverzió:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Fájl olvasás (XML) → XML Parser → Database Insert
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Config feldolgozás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              XML Parser → Data Transform → CRM Action
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default XMLParserConfig;
